import React, { useCallback, useState } from 'react';

import ANALYTICS_CATEGORIES from '../../analytics/categories';
import PaymentDetailsModal from '../../components/PaymentDetailsModal/PaymentDetailsModal';
import PaymentItemModalContent from '../../components/transaction/PaymentItem/PaymentItemModalContent';
import { useConfig } from '../../defaultConfig';
import useAccountChange from '../../fetchHooks/useAccountChange';
import type { PaymentItem } from '../../generated-types';
import { useActorReceiver } from '../../hooks/useSetReceiver';
import { SigningContextProvider, useSigningContext } from '../../signingContext/signingContext';
import { Actions } from '../../signingContext/signingTypes';
import StoppedAvtalegiroForm from './StoppedAvtalegiroForm';
import StoppedPaymentDetailsView from './StoppedPaymentDetailsView';
import useGuessAvtalegiroFromStoppedPayment from './useGuessAvtalegiroFromStoppedPayment';

type Props = {
    selectedPaymentItem: PaymentItem | undefined;
    resetSelectedItem: () => void;
};

const StoppedPaymentDetailsModal: React.FC<Props> = ({ selectedPaymentItem, resetSelectedItem }) => {
    const openEditModalState = useState(false);
    const [, setOpenEditModal] = openEditModalState;
    const [isSuccess, setIsSuccess] = useState(false);

    const { featureToggles } = useConfig();

    const receiver = useActorReceiver(selectedPaymentItem?.receiver);
    const refresh = useAccountChange();
    const { handleCancelSigningFlow, dispatch, signingStatus, dynamicLinkId } = useSigningContext();
    const { optimisticAvtalegiro, optimisticAvtalegiroDetails, invalidateQuery, isLoading } =
        useGuessAvtalegiroFromStoppedPayment(selectedPaymentItem, receiver);

    const shouldEditAvtalegiro = selectedPaymentItem?.subStatus === 'EXCEEDING_MAX_LIMIT';

    const isAvtalegiroAndShouldEdit =
        !!selectedPaymentItem &&
        selectedPaymentItem.paymentItemType === 'AUTO_PAYMENT' &&
        !!optimisticAvtalegiroDetails &&
        !!optimisticAvtalegiro &&
        shouldEditAvtalegiro;

    const isPotentialAvtalegiro =
        selectedPaymentItem?.paymentItemType === 'AUTO_PAYMENT' &&
        isLoading &&
        featureToggles.enableStoppedAvtalegiroEdit;

    const onCloseAndResetSigningState = useCallback(() => {
        setIsSuccess(false);
        if (signingStatus === 'PROCESSING' && dynamicLinkId) {
            handleCancelSigningFlow();
        }
        refresh();
        setOpenEditModal(false);
        dispatch({ type: Actions.RESET });
        if (isAvtalegiroAndShouldEdit) {
            invalidateQuery();
        }
        resetSelectedItem();
    }, [
        handleCancelSigningFlow,
        dispatch,
        resetSelectedItem,
        setOpenEditModal,
        dynamicLinkId,
        refresh,
        signingStatus,
        invalidateQuery,
        isAvtalegiroAndShouldEdit,
    ]);

    return (
        <PaymentDetailsModal
            id="stopped-payment-details"
            openEditModalState={openEditModalState}
            open={!!selectedPaymentItem}
            onClose={onCloseAndResetSigningState}
            editModalChildren={
                <PaymentItemModalContent
                    paymentItem={selectedPaymentItem}
                    id={selectedPaymentItem?.paymentItemId}
                    onClose={onCloseAndResetSigningState}
                />
            }
            isAvtalegiro={isAvtalegiroAndShouldEdit}
            isSuccess={isSuccess || signingStatus === 'COMPLETED'}
        >
            {isPotentialAvtalegiro || isAvtalegiroAndShouldEdit ? (
                <StoppedAvtalegiroForm
                    paymentItem={selectedPaymentItem}
                    optimisticAvtalegiroDetails={optimisticAvtalegiroDetails}
                    receiver={receiver}
                    onClose={onCloseAndResetSigningState}
                    avtalegiroId={optimisticAvtalegiro?.id}
                    setIsSuccess={setIsSuccess}
                    isLoading={isLoading}
                />
            ) : (
                <StoppedPaymentDetailsView
                    paymentItem={selectedPaymentItem}
                    openEditModalState={openEditModalState}
                    onClose={onCloseAndResetSigningState}
                    setIsSuccess={setIsSuccess}
                />
            )}
        </PaymentDetailsModal>
    );
};

export default (props: Props) => (
    <SigningContextProvider analyticsCategory={ANALYTICS_CATEGORIES.UPDATE_PAYMENT_ITEM}>
        <StoppedPaymentDetailsModal {...props} />
    </SigningContextProvider>
);
